import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineClock,
  HiOutlineSearch,
  HiOutlineRefresh,
  HiOutlineX,
  HiOutlineTicket,
  HiOutlineExclamationCircle,
} from "react-icons/hi";
import { useAppDispatch } from "reduxFiles/hooks";
import { useGetTransactionStatusQuery } from "reduxFiles/api/assessmentApi";
import { TransactionDTO } from "types/assessmentTypes";
import Button from "components/button/Button";

type PaymentStatus =
  | "loading"
  | "pending"
  | "success"
  | "failed"
  | "error"
  | "notFound";

// Status configuration for different payment states
const STATUS_CONFIG = {
  loading: {
    icon: (
      <HiOutlineClock className="animate-spin h-12 w-12 text-blue-600 dark:text-blue-400" />
    ),
    bgColor: "bg-blue-100 dark:bg-blue-500/20",
  },
  pending: {
    icon: (
      <HiOutlineClock className="animate-pulse h-12 w-12 text-yellow-600 dark:text-yellow-400" />
    ),
    bgColor: "bg-yellow-100 dark:bg-yellow-500/20",
  },
  success: {
    icon: (
      <HiOutlineCheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
    ),
    bgColor: "bg-green-100 dark:bg-green-500/20",
  },
  failed: {
    icon: (
      <HiOutlineXCircle className="h-12 w-12 text-red-600 dark:text-red-400" />
    ),
    bgColor: "bg-red-100 dark:bg-red-500/20",
  },
  error: {
    icon: (
      <HiOutlineExclamationCircle className="h-12 w-12 text-orange-600 dark:text-orange-400" />
    ),
    bgColor: "bg-orange-100 dark:bg-orange-500/20",
  },
  notFound: {
    icon: (
      <HiOutlineSearch className="h-12 w-12 text-orange-600 dark:text-orange-400" />
    ),
    bgColor: "bg-orange-100 dark:bg-orange-500/20",
  },
};

const PaymentStatusModal = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const transactionId = searchParams.get("transactionId");

  const [transactionDetails, setTransactionDetails] =
    useState<TransactionDTO | null>(null);
  const [status, setStatus] = useState<PaymentStatus>("loading");
  const [message, setMessage] = useState("");
  const [isRetryable, setIsRetryable] = useState(false);

  // Fetch transaction status
  const {
    data: transactionData,
    isLoading,
    error,
    refetch,
  } = useGetTransactionStatusQuery(Number(transactionId), {
    skip: !transactionId,
    refetchOnMountOrArgChange: true,
  });

  // Handle loading state
  useEffect(() => {
    if (isLoading) {
      setStatus("loading");
      setMessage(t("paymentCallback.loadingMessage"));
      setIsRetryable(false);
    }
  }, [isLoading, t]);

  // Handle transaction data update
  useEffect(() => {
    if (transactionData) {
      setTransactionDetails(transactionData);

      switch (transactionData.status) {
        case "SUCCESS":
          setStatus("success");
          setIsRetryable(false);
          setMessage(
            t("paymentCallback.successMessage", {
              count: transactionData.quantity,
            })
          );
          localStorage.removeItem("pendingTransaction");
          break;

        case "FAILED":
          setStatus("failed");
          setIsRetryable(false);
          setMessage(t("paymentCallback.failedMessage"));
          break;

        case "PENDING":
          setStatus("pending");
          setIsRetryable(true);
          setMessage(t("paymentCallback.pendingMessage"));
          break;

        default:
          setStatus("notFound");
          setIsRetryable(false);
          setMessage(
            t("paymentCallback.notFoundMessage", { id: transactionId })
          );
      }
    }
  }, [transactionData, t, dispatch, transactionId]);

  // Handle error states
  useEffect(() => {
    if (error && !isLoading) {
      const isNotFound = "status" in error && error.status === 404;
      const isNetworkError = "status" in error && !error.status;

      if (isNotFound) {
        setStatus("notFound");
        setIsRetryable(false);
        setMessage(t("paymentCallback.notFoundMessage", { id: transactionId }));
      } else if (isNetworkError) {
        setStatus("error");
        setIsRetryable(true);
        setMessage(t("paymentCallback.networkErrorMessage"));
      } else {
        setStatus("error");
        setIsRetryable(true);
        setMessage(t("paymentCallback.serverErrorMessage"));
      }
    }
  }, [error, isLoading, t, dispatch, transactionId]);

  // Close modal and remove transactionId from URL
  const handleClose = () => {
    searchParams.delete("transactionId");
    setSearchParams(searchParams, { replace: true });
  };

  // If no transactionId, don't render modal
  if (!transactionId) return null;
  const config = STATUS_CONFIG[status];

  const getTitle = () => {
    switch (status) {
      case "loading":
        return t("paymentCallback.loadingTitle");
      case "pending":
        return t("paymentCallback.pendingTitle");
      case "success":
        return t("paymentCallback.successTitle");
      case "failed":
        return t("paymentCallback.failedTitle");
      case "error":
        return t("paymentCallback.errorTitle");
      case "notFound":
        return t("paymentCallback.notFoundTitle");
      default:
        return t("paymentCallback.unknownTitle");
    }
  };

  const renderActionButtons = () => {
    if (status === "loading") {
      return null;
    }

    if (status === "success") {
      return (
        <Button variant="secondary" onClick={handleClose} fullWidth>
          {t("buttons.close")}
        </Button>
      );
    }

    if (status === "failed") {
      return (
        <Button variant="secondary" onClick={handleClose} fullWidth>
          {t("buttons.close")}
        </Button>
      );
    }

    if (isRetryable) {
      return (
        <>
          <Button
            variant="primary"
            onClick={() => refetch()}
            fullWidth
            loading={isLoading}
            startIcon={<HiOutlineRefresh className="h-4 w-4" />}
          >
            {t("paymentCallback.checkAgain")}
          </Button>
          <Button
            variant="secondary"
            className="mt-2"
            onClick={handleClose}
            fullWidth
          >
            {t("buttons.close")}
          </Button>
        </>
      );
    }

    // Fallback
    return (
      <Button variant="secondary" onClick={handleClose} fullWidth>
        {t("buttons.close")}
      </Button>
    );
  };

  const showCloseButton = status !== "loading";

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="bg-black/50 absolute inset-0 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          key={status}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3 }}
          className="relative w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-2xl dark:bg-navy-800"
        >
          {/* Close button */}
          {showCloseButton && (
            <button
              onClick={handleClose}
              className="absolute right-3 top-3 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
            >
              <HiOutlineX className="h-5 w-5" />
            </button>
          )}

          {/* Status icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
            className={`mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full ${config.bgColor}`}
          >
            {config.icon}
          </motion.div>

          {/* Title */}
          <h1 className="mb-1 text-lg font-bold text-gray-900 dark:text-white">
            {getTitle()}
          </h1>

          {/* Message */}
          <p className="mb-3 text-xs text-gray-700 dark:text-gray-400">
            {message}
          </p>

          {/* Transaction details */}
          {(status === "success" ||
            status === "pending" ||
            status === "failed" ||
            status === "error") &&
            transactionDetails && (
              <div className="mb-4 overflow-hidden rounded-xl bg-gray-50 p-3 text-start text-xs dark:bg-gray-800/50">
                <div className="space-y-1.5">
                  {/* Purchase type */}
                  <div className="flex justify-between border-b border-gray-200 pb-1.5 dark:border-gray-700">
                    <span className="text-gray-700">
                      {transactionDetails.organizationId
                        ? t("paymentCallback.organizationPurchase")
                        : t("paymentCallback.personalPurchase")}
                    </span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">
                      {transactionDetails.organizationId
                        ? transactionDetails.organization?.name ||
                          t("paymentCallback.unknown")
                        : transactionDetails.user?.fullname ||
                          `${t("paymentCallback.user")} ${
                            transactionDetails.userId
                          }`}
                    </span>
                  </div>

                  {/* Transaction ID */}
                  <div className="flex justify-between">
                    <span className="text-gray-700">
                      {t("paymentCallback.transactionId")}
                    </span>
                    <span className="font-mono text-gray-800 dark:text-gray-200">
                      {transactionDetails.id}
                    </span>
                  </div>

                  {/* Reference ID */}
                  {transactionDetails.refId && (
                    <div className="flex justify-between">
                      <span className="text-gray-700">
                        {t("paymentCallback.refId")}
                      </span>
                      <span className="font-mono text-gray-800 dark:text-gray-200">
                        {transactionDetails.refId}
                      </span>
                    </div>
                  )}

                  {/* Amount */}
                  <div className="flex justify-between">
                    <span className="text-gray-700">
                      {t("paymentCallback.amount")}
                    </span>
                    <span className="font-bold text-brand-700">
                      {transactionDetails.amount.toLocaleString(
                        lang === "fa" ? "fa-IR" : "en-US"
                      )}{" "}
                      {t("paymentCallback.priceInit")}
                    </span>
                  </div>

                  {/* Quantity */}
                  <div className="flex justify-between">
                    <span className="text-gray-700">
                      {t("paymentCallback.quantity")}
                    </span>
                    <span className="text-gray-800 dark:text-gray-200">
                      {transactionDetails.quantity}{" "}
                      {t("paymentCallback.license")}
                    </span>
                  </div>

                  {/* Status badge */}
                  <div className="flex justify-between">
                    <span className="text-gray-700">
                      {t("paymentCallback.status")}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        status === "success"
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : status === "pending"
                          ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                          : status === "failed"
                          ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                          : status === "error"
                          ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                          : "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400"
                      }`}
                    >
                      {status === "success"
                        ? t("paymentCallback.statusSuccess")
                        : status === "pending"
                        ? t("paymentCallback.statusPending")
                        : status === "failed"
                        ? t("paymentCallback.statusFailed")
                        : status === "error"
                        ? t("paymentCallback.statusError")
                        : t("paymentCallback.statusUnknown")}
                    </span>
                  </div>

                  {/* Created at */}
                  <div className="flex justify-between">
                    <span className="text-gray-700">
                      {t("paymentCallback.createdAt")}
                    </span>
                    <span className="text-gray-800 dark:text-gray-200">
                      {new Date(
                        transactionDetails.createdAt
                      ).toLocaleDateString(lang === "fa" ? "fa-IR" : "en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>

                  {/* Purchased licenses */}
                  {status === "success" &&
                    transactionDetails.licenses?.length > 0 && (
                      <div className="mt-2 border-t border-gray-200 pt-2 dark:border-gray-700">
                        <div className="mb-1 text-gray-700">
                          {t("paymentCallback.purchasedLicenses")}
                        </div>
                        <div className="custom-scrollbar max-h-24 space-y-0.5 overflow-y-auto">
                          {transactionDetails.licenses.map((license) => (
                            <div
                              key={license.id}
                              className="flex items-center gap-1 text-xs"
                            >
                              <HiOutlineTicket className="h-3 w-3 shrink-0 text-brand-500" />
                              <span className="break-all font-mono text-gray-700 dark:text-gray-300">
                                {license.licenseKey}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            )}

          {/* Action buttons */}
          {renderActionButtons()}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PaymentStatusModal;
